import '@awesome.me/webawesome/dist/components/button/button.js';

import { ENGAGEMENT_CONFIG } from '@app/core';
import { useCallback, useEffect, useRef } from 'react';

import { CardPositionIndicator } from './components/CardPositionIndicator.js';
import { DebugPanel } from './components/DebugPanel.js';
import { EngagementShell } from './components/EngagementShell.js';
import { FocusCard } from './components/FocusCard.js';
import { OnboardingFlow } from './components/onboarding/OnboardingFlow.js';
import { QueuePreview } from './components/QueuePreview.js';
import { QuickAdd } from './components/QuickAdd.js';
import { Timeline } from './components/Timeline.js';
import { YouAreClear } from './components/YouAreClear.js';
import {
  db,
  DexieInteractionRepository,
  DexieItemRepository,
  DexieSessionRepository,
  DexieUserRepository,
  ItemService,
} from './services/index.js';
import { SimulatedTimeProvider } from './services/time-provider.js';
import { useItemsStore } from './stores/items-store.js';
import { useSessionStore } from './stores/session-store.js';
import { useUIStore } from './stores/ui-store.js';
import { useUserStore } from './stores/user-store.js';

// Singleton service instances
const timeProvider = new SimulatedTimeProvider();
const itemRepo = new DexieItemRepository(db);
const sessionRepo = new DexieSessionRepository(db);
const interactionRepo = new DexieInteractionRepository(db);
const userRepo = new DexieUserRepository(db);
const itemService = new ItemService(itemRepo, timeProvider);

export function App() {
  const { screen } = useUIStore();
  const { user, loading: userLoading, init: initUser } = useUserStore();
  const {
    engagementLevel,
    currentSession,
    loading: sessionLoading,
    init: initSession,
  } = useSessionStore();
  const {
    queue,
    focusIndex,
    loading: itemsLoading,
    loadQueue,
    complete,
    snooze,
    dismiss,
    reheat,
    skipTo,
  } = useItemsStore();
  const openQuickAdd = useUIStore(s => s.openQuickAdd);

  const initialized = useRef(false);

  // Initialization
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    void (async () => {
      await initUser(userRepo);
    })();
  }, [initUser]);

  // After user loads, init session
  useEffect(() => {
    if (userLoading || !user) return;
    void initSession(sessionRepo, timeProvider, user.hasOnboarded);
  }, [userLoading, user, initSession]);

  // After session loads, load queue
  useEffect(() => {
    if (sessionLoading || userLoading) return;
    void loadQueue(itemService, engagementLevel);
  }, [sessionLoading, userLoading, engagementLevel, loadQueue]);

  // After user onboards, set screen
  useEffect(() => {
    if (!user) return;
    if (!user.hasOnboarded) {
      useUIStore.getState().setScreen('onboarding');
    }
  }, [user]);

  const handleTimeChange = useCallback(() => {
    void loadQueue(itemService, engagementLevel);
  }, [loadQueue, engagementLevel]);

  const handleItemAdded = useCallback(() => {
    void loadQueue(itemService, engagementLevel);
  }, [loadQueue, engagementLevel]);

  const sessionId = currentSession?.id ?? '';
  const config = ENGAGEMENT_CONFIG[engagementLevel];
  const focus = queue[focusIndex];
  const remaining = queue.slice(focusIndex + 1);

  if (userLoading || sessionLoading) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    );
  }

  if (screen === 'onboarding') {
    return (
      <EngagementShell engagement="new">
        <OnboardingFlow itemRepo={itemRepo} userRepo={userRepo} />
      </EngagementShell>
    );
  }

  return (
    <EngagementShell engagement={engagementLevel}>
      {itemsLoading ? null : focus ? (
        <>
          <FocusCard
            surfaced={focus}
            onComplete={() =>
              void complete(itemService, interactionRepo, engagementLevel, sessionId, timeProvider)
            }
            onSnooze={() =>
              void snooze(itemService, interactionRepo, engagementLevel, sessionId, timeProvider)
            }
            onDismiss={() =>
              void dismiss(itemService, interactionRepo, engagementLevel, sessionId, timeProvider)
            }
            onReheat={() =>
              void reheat(itemService, interactionRepo, engagementLevel, sessionId, timeProvider)
            }
          />
          <CardPositionIndicator total={queue.length} current={focusIndex} />
          <QueuePreview
            items={remaining}
            maxVisible={config.maxQueueVisible}
            onSelect={i =>
              void skipTo(i, itemService, interactionRepo, engagementLevel, sessionId, timeProvider)
            }
          />
          <Timeline items={queue} visible={config.showTimeline} />
        </>
      ) : (
        <YouAreClear />
      )}

      <button
        onClick={openQuickAdd}
        aria-label="Quick add"
        style={{
          position: 'fixed',
          bottom: 'var(--space-xl)',
          right: 'var(--space-xl)',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'var(--color-cool)',
          border: 'none',
          color: 'var(--color-bg)',
          fontSize: '1.5rem',
          cursor: 'pointer',
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
          zIndex: 50,
        }}
      >
        +
      </button>

      {screen === 'quick-add' && <QuickAdd itemRepo={itemRepo} onAdded={handleItemAdded} />}

      <DebugPanel
        queue={queue}
        timeProvider={timeProvider}
        onTimeChange={handleTimeChange}
        database={db}
      />
    </EngagementShell>
  );
}
